import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, downloadGet, get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Textarea, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { InsuranceModePanel } from '../insurance/InsuranceModePanel';

interface BranchLicence {
  id: string;
  code: string;
  name: string;
  country: string | null;
  nphiesProviderLicense: string | null;
  nphiesProviderTypeCode: string | null;
  nphiesLocationLicense: string | null;
  nphiesLocationRoleCode: string | null;
}

interface PracticeCode {
  id: string;
  code: string;
  name: string;
  nphiesPracticeCode: string | null;
}

function BranchLicenceRow({ row }: { row: BranchLicence }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    providerLicense: row.nphiesProviderLicense ?? '',
    providerTypeCode: row.nphiesProviderTypeCode ?? '',
    locationLicense: row.nphiesLocationLicense ?? '',
    locationRoleCode: row.nphiesLocationRoleCode ?? '',
  });
  const save = useMutation({
    mutationFn: () =>
      put(`/integrations/nphies/branch-licences/${row.id}`, {
        providerLicense: form.providerLicense.trim() || null,
        providerTypeCode: form.providerTypeCode.trim() || null,
        locationLicense: form.locationLicense.trim() || null,
        locationRoleCode: form.locationRoleCode.trim() || null,
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'x_saved_2', { name: row.name }));
      void queryClient.invalidateQueries({ queryKey: ['nphies-branch-licences'] });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_save'), describeError(caught)),
  });
  return (
    <div className="grid items-end gap-2 border-b py-2 sm:grid-cols-6">
      <span className="text-sm sm:col-span-1">
        {row.name}
        {row.country && row.country !== 'SA' ? <span className="block text-xs text-muted-foreground">{tr('admin', 'outside_saudi_arabia_not_sent_to_nphies')}</span> : null}
      </span>
      <Input placeholder={tr('admin', 'provider_licence')} value={form.providerLicense} onChange={(e) => setForm({ ...form, providerLicense: e.target.value })} />
      <Input placeholder={tr('admin', 'provider_type')} value={form.providerTypeCode} onChange={(e) => setForm({ ...form, providerTypeCode: e.target.value })} />
      <Input placeholder={tr('admin', 'location_licence')} value={form.locationLicense} onChange={(e) => setForm({ ...form, locationLicense: e.target.value })} />
      <Input placeholder={tr('admin', 'location_role')} value={form.locationRoleCode} onChange={(e) => setForm({ ...form, locationRoleCode: e.target.value })} />
      <Button size="sm" variant="outline" loading={save.isPending} onClick={() => save.mutate()}>
        {tr('admin', 'save')}
      </Button>
    </div>
  );
}

function PracticeCodeRow({ row }: { row: PracticeCode }) {
  const toast = useToast();
  const [code, setCode] = useState(row.nphiesPracticeCode ?? '');
  const save = useMutation({
    mutationFn: () => put(`/integrations/nphies/practice-codes/${row.id}`, { practiceCode: code.trim() || null }),
    onSuccess: () => toast.success(tr('admin', 'x_saved_2', { name: row.name })),
    onError: (caught) => toast.error(tr('admin', 'could_not_save'), describeError(caught)),
  });
  return (
    <span className="flex items-center gap-2">
      <Input className="w-28" value={code} onChange={(e) => setCode(e.target.value)} />
      <Button size="sm" variant="ghost" loading={save.isPending} onClick={() => save.mutate()}>
        {tr('admin', 'save')}
      </Button>
    </span>
  );
}

interface CertificationSession {
  active: boolean;
  startedAt: string | null;
  expiresAt: string | null;
  entries: { id: string; direction: 'sent' | 'received'; operation: string; correlationId: string; recordedAt: string; sizeBytes: number; sha256: string; complete: boolean }[];
}

function CertificationSessionPanel() {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ['nphies-certification-session'], queryFn: () => get<CertificationSession>('/integrations/nphies/certification-session') });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['nphies-certification-session'] });
  const start = useMutation({
    mutationFn: () => post<CertificationSession>('/integrations/nphies/certification-session'),
    onSuccess: () => refresh(),
    onError: (caught) => toast.error(tr('admin', 'could_not_start_the_session'), describeError(caught)),
  });
  const stop = useMutation({ mutationFn: () => del('/integrations/nphies/certification-session'), onSuccess: () => refresh() });
  const data = session.data;
  return (
    <div>
      <p className="mb-1 text-sm font-medium">{tr('admin', 'certification_session')}</p>
      <p className="mb-2 text-xs text-muted-foreground">
        {tr('admin', 'while_a_session_is_on_the_last_20_messages')}
      </p>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {data?.active ? (
          <>
            <span className="text-xs">{tr('admin', 'on_until')} {data.expiresAt ? new Date(data.expiresAt).toLocaleTimeString() : ''}</span>
            <Button size="sm" variant="outline" onClick={() => refresh()}>
              {tr('admin', 'refresh')}
            </Button>
            <Button size="sm" variant="ghost" loading={stop.isPending} onClick={() => stop.mutate()}>
              {tr('admin', 'stop')}
            </Button>
          </>
        ) : (
          isSuperAdmin && (
            <Button size="sm" variant="outline" loading={start.isPending} onClick={() => start.mutate()}>
              {tr('admin', 'start_a_session')}
            </Button>
          )
        )}
      </div>
      {data?.active && (
        <DataTable
          dense
          rows={data.entries}
          rowKey={(row) => row.id}
          empty={tr('admin', 'nothing_exchanged_yet_in_this_session')}
          columns={[
            { header: tr('admin', 'when'), sort: (row) => row.recordedAt, cell: (row) => new Date(row.recordedAt).toLocaleTimeString() },
            { header: tr('admin', 'message'), sort: (row) => row.operation, cell: (row) => `${row.direction === 'sent' ? 'Sent' : 'Answer'} · ${statusLabel(row.operation)}` },
            { header: tr('admin', 'size'), sort: (row) => row.sizeBytes, cell: (row) => `${Math.ceil(row.sizeBytes / 1024)} KB${row.complete ? '' : ' (start only)'}` },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <Button size="sm" variant="ghost" onClick={() => void downloadGet(`/integrations/nphies/certification-session/messages/${row.id}`, `nphies-${row.operation}-${row.direction}-${row.correlationId}.json`).catch((caught) => toast.error(tr('admin', 'could_not_download_it'), describeError(caught)))}>
                  {tr('admin', 'download')}
                </Button>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

interface ArchiveRow {
  id: string;
  entityType: string | null;
  entityId: string | null;
  operation: string;
  direction: string;
  messageId: string;
  sizeBytes: number;
  createdAt: string;
}

export function NphiesArchivePanel() {
  const toast = useToast();
  const [cursor, setCursor] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['nphies-archive', cursor],
    queryFn: () => get<{ items: ArchiveRow[]; nextCursor: string | null }>(`/integrations/nphies/archive?limit=50${cursor ? `&cursor=${cursor}` : ''}`),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('admin', 'nphies_message_archive')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {tr('admin', 'every_message_sent_to_and_received_from_nphies')}
        </p>
        <DataTable
          dense
          rows={list.data?.items}
          loading={list.isLoading} refreshing={list.isFetching}
          error={list.error}
          rowKey={(row) => row.id}
          empty={tr('admin', 'nothing_has_been_exchanged_yet')}
          columns={[
            { header: tr('admin', 'when'), sort: (row) => row.createdAt, cell: (row) => new Date(row.createdAt).toLocaleString() },
            { header: tr('admin', 'message'), sort: (row) => row.operation, cell: (row) => `${row.direction === 'sent' ? 'Sent' : 'Received'} · ${statusLabel(row.operation)}` },
            { header: tr('admin', 'about'), sort: (row) => row.entityType, cell: (row) => <span className="text-xs text-muted-foreground">{row.entityType ? humanize(row.entityType) : ''}</span> },
            { header: tr('admin', 'size'), sort: (row) => row.sizeBytes, cell: (row) => `${Math.ceil(row.sizeBytes / 1024)} KB` },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <Button size="sm" variant="ghost" onClick={() => void downloadGet(`/integrations/nphies/archive/${row.id}/download`, `nphies-${row.operation}-${row.direction}.json`).catch((caught) => toast.error(tr('admin', 'could_not_download_it'), describeError(caught)))}>
                  {tr('admin', 'download')}
                </Button>
              ),
            },
          ]}
          footer={
            (cursor || list.data?.nextCursor) && (
              <div className="ms-auto flex gap-2">
                {cursor && (
                  <Button size="sm" variant="ghost" onClick={() => setCursor(null)}>
                    {tr('admin', 'newest')}
                  </Button>
                )}
                {list.data?.nextCursor && (
                  <Button size="sm" variant="outline" onClick={() => setCursor(list.data?.nextCursor ?? null)}>
                    {tr('admin', 'older')}
                  </Button>
                )}
              </div>
            )
          }
        />
      </CardContent>
    </Card>
  );
}

export function NphiesSetupPanels() {
  const branches = useQuery({ queryKey: ['nphies-branch-licences'], queryFn: () => get<BranchLicence[]>('/integrations/nphies/branch-licences') });
  const practice = useQuery({ queryKey: ['nphies-practice-codes'], queryFn: () => get<PracticeCode[]>('/integrations/nphies/practice-codes') });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('admin', 'nphies_implementation_team')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="mb-1 text-sm font-medium">{tr('admin', 'licences_per_branch')}</p>
          <p className="mb-2 text-xs text-muted-foreground">{tr('admin', 'each_branch_sends_under_its_own_provider')}</p>
          {branches.data?.map((row) => <BranchLicenceRow key={row.id} row={row} />)}
        </div>
        <div>
          <p className="mb-1 text-sm font-medium">{tr('admin', 'practice_code_per_specialty')}</p>
          <DataTable
            dense
            rows={practice.data}
            loading={practice.isLoading} refreshing={practice.isFetching}
            error={practice.error}
            rowKey={(row) => row.id}
            empty={tr('admin', 'no_specialties_on_file')}
            columns={[
              { header: tr('admin', 'specialty'), sort: (row) => row.name, cell: (row) => row.name },
              { header: tr('admin', 'practice_code'), cell: (row) => <PracticeCodeRow row={row} /> },
            ]}
          />
        </div>
        <div>
          <p className="mb-1 text-sm font-medium">{tr('admin', 'insurance_authorisation_mode')}</p>
          <InsuranceModePanel embedded />
        </div>
        <CertificationSessionPanel />
      </CardContent>
    </Card>
  );
}

interface ReplyTemplateRow {
  id: string;
  title: string;
  body: string;
  isActive: boolean;
}

export function NphiesReplyTemplatesPanel() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ id: string | null; title: string; body: string; isActive: boolean } | null>(null);
  const templates = useQuery({ queryKey: ['nphies-reply-templates', 'all'], queryFn: () => get<ReplyTemplateRow[]>('/integrations/nphies/reply-templates?all=true') });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['nphies-reply-templates'] });
  const save = useMutation({
    mutationFn: (form: { id: string | null; title: string; body: string; isActive: boolean }) =>
      form.id
        ? put<ReplyTemplateRow>(`/integrations/nphies/reply-templates/${form.id}`, { title: form.title.trim(), body: form.body.trim(), isActive: form.isActive })
        : post<ReplyTemplateRow>('/integrations/nphies/reply-templates', { title: form.title.trim(), body: form.body.trim(), isActive: form.isActive }),
    onSuccess: () => {
      toast.success(tr('admin', 'template_saved'));
      setEditing(null);
      refresh();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_template'), describeError(error)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/integrations/nphies/reply-templates/${id}`),
    onSuccess: () => {
      toast.success(tr('admin', 'template_removed'));
      refresh();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_remove_the_template'), describeError(error)),
  });
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-sm">{tr('admin', 'reply_templates_for_insurer_requests')}</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setEditing({ id: null, title: '', body: '', isActive: true })}>
          {tr('admin', 'add_a_template')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing && (
          <div className="space-y-2 rounded-md border p-3">
            <Input placeholder={tr('admin', 'title_for_example_discharge_summary_attached')} value={editing.title} maxLength={120} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            <Textarea rows={3} maxLength={2000} placeholder={tr('admin', 'the_reply_text')} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            <Checkbox checked={editing.isActive} onCheckedChange={(isActive) => setEditing({ ...editing, isActive })} label={tr('admin', 'offered_when_answering')} />
            <div className="flex gap-2">
              <Button size="sm" loading={save.isPending} disabled={editing.title.trim().length < 2 || editing.body.trim().length < 3} onClick={() => save.mutate(editing)}>
                {tr('admin', 'save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                {tr('admin', 'cancel')}
              </Button>
            </div>
          </div>
        )}
        <DataTable
          dense
          rows={templates.data}
          loading={templates.isLoading} refreshing={templates.isFetching}
          error={templates.error}
          rowKey={(row) => row.id}
          empty={tr('admin', 'no_reply_templates_yet')}
          columns={[
            { header: tr('admin', 'title'), sort: (row) => row.title, cell: (row) => <span className={row.isActive ? '' : 'text-muted-foreground line-through'}>{row.title}</span> },
            { header: tr('admin', 'text'), cell: (row) => <span className="line-clamp-2 max-w-md text-xs">{row.body}</span> },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ id: row.id, title: row.title, body: row.body, isActive: row.isActive })}>
                    {tr('admin', 'edit')}
                  </Button>
                  <Button size="sm" variant="ghost" loading={remove.isPending && remove.variables === row.id} onClick={() => remove.mutate(row.id)}>
                    {tr('admin', 'remove')}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
